import React from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@fluentui/react-components';
import theme from '../../../../theme/theme';
import AlyzePlusLogo from '../../../../assets/ALYZE+_Logo.svg';
import AlyzePlusLogoGray from '../../../../assets/ALYZE+_Logo_Gray.svg';

interface PriceSectionProps {
    hasMembership: boolean;
    retailPrice: number;
    membershipPrice: number;
    showMembershipLabel?: boolean;
    size?: 'normal' | 'large';
    align?: 'left' | 'center' | 'right';
    subscriptionInterval?: string;
}

const useStyles = makeStyles({
    priceSection: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
    },

    priceSectionLeft: {
        alignItems: 'flex-start',
    },

    priceSectionCenter: {
        alignItems: 'center',
    },

    priceSectionRight: {
        alignItems: 'flex-end',
    },

    priceRow: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
    },

    priceRowRight: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
        justifyContent: 'flex-end',
    },

    currentPrice: {
        color: theme.colorNeutralForeground1,
        fontSize: '20px',
        fontFamily: 'Figtree',
        fontWeight: '400',
        lineHeight: '30px',
        '@media (max-width: 768px)': {
            fontSize: '24px',
            lineHeight: '32px',
        },
    },

    currentPriceSmall: {
        fontSize: '18px',
        '@media (max-width: 768px)': {
            fontSize: '18px',
            lineHeight: '24px',
        },
    },

    strikethroughPrice: {
        color: '#707070',
        fontSize: '18px',
        fontFamily: 'Figtree',
        fontWeight: '400',
        textDecoration: 'line-through',
        lineHeight: '30px',
        '@media (max-width: 768px)': {
            fontSize: '18px',
            lineHeight: '24px',
        },
    },

    strikethroughPriceSmall: {
        fontSize: '18px',
        '@media (max-width: 768px)': {
            fontSize: '16px',
            lineHeight: '20px',
        },
    },

    alyzeLogo: {
        width: '60px'
    },

    alyzeLogoSmall: {
        width: '60px'
    },

    membershipText: {
        color: theme.colorNeutralForeground1,
        fontSize: '16px',
        fontFamily: 'Figtree',
        fontWeight: '700',
        lineHeight: '22px',
        '@media (max-width: 768px)': {
            fontSize: '14px',
            lineHeight: '20px',
        },
    },

    membershipTextSmall: {
        fontSize: '14px',
        '@media (max-width: 768px)': {
            fontSize: '12px',
            lineHeight: '16px',
        },
    },

    membershipIndicator: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '6px',
    },

    membershipIndicatorRight: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '6px',
    },

    membershipIndicatorInline: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginLeft: '8px',
    },

    membershipIndicatorInlineRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginRight: '8px',
    },

    subscriptionInterval: {
        fontSize: '16px',
    },

    membershipPriceLink: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
        '&:hover': {
            opacity: 0.8,
        },
    },

    membershipIndicatorLink: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        textDecoration: 'none',
        color: 'inherit',
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
        '&:hover': {
            opacity: 0.8,
        },
    },
});

export const PriceSection: React.FC<PriceSectionProps> = ({
    hasMembership,
    retailPrice,
    membershipPrice,
    showMembershipLabel = false,
    size = 'normal',
    align = 'left',
    subscriptionInterval,
}) => {
    const styles = useStyles();

    // Handle alignment classes
    let alignmentClass = styles.priceSectionLeft;
    let priceRowClass = styles.priceRow;
    let membershipIndicatorClass = styles.membershipIndicator;
    let membershipIndicatorInlineClass = styles.membershipIndicatorInline;

    if (align === 'center') {
        alignmentClass = styles.priceSectionCenter;
    } else if (align === 'right') {
        alignmentClass = styles.priceSectionRight;
        priceRowClass = styles.priceRowRight;
        membershipIndicatorClass = styles.membershipIndicatorRight;
        membershipIndicatorInlineClass = styles.membershipIndicatorInlineRight;
    }

    const isSmall = size === 'normal';
    const currentPriceClass = isSmall ? `${styles.currentPrice} ${styles.currentPriceSmall}` : styles.currentPrice;
    const strikethroughPriceClass = isSmall ? `${styles.strikethroughPrice} ${styles.strikethroughPriceSmall}` : styles.strikethroughPrice;
    const logoClass = isSmall ? styles.alyzeLogoSmall : styles.alyzeLogo;
    const membershipTextClass = isSmall ? `${styles.membershipText} ${styles.membershipTextSmall}` : styles.membershipText;

    const sectionClass = `${styles.priceSection} ${alignmentClass}`;

    // Check if prices are the same - if so, only show retail price
    const pricesAreDifferent = retailPrice !== membershipPrice;
    const showMembershipPricing = hasMembership && pricesAreDifferent;

    if (!showMembershipLabel) {
        return (
            <div className={sectionClass}>
                <div className={priceRowClass}>
                    {showMembershipPricing ? (
                        <>
                            <span className={strikethroughPriceClass}>
                                Retail: ${retailPrice.toFixed(2)}
                            </span>
                            <Link to="/v1/memberships" className={styles.membershipPriceLink}>
                                <span className={currentPriceClass}>
                                    ${membershipPrice.toFixed(2)}
                                </span>
                            </Link>
                        </>
                    ) : (
                        <>
                            <span className={currentPriceClass}>
                                Retail: ${retailPrice.toFixed(2)}
                            </span>
                        </>
                    )}
                </div>
                {pricesAreDifferent && (
                    <Link to="/v1/memberships" className={`${membershipIndicatorInlineClass} ${styles.membershipIndicatorLink}`}>
                        <img className={logoClass} src={showMembershipPricing ? AlyzePlusLogo : AlyzePlusLogoGray} alt="ALYZE+" />
                        <span className={membershipTextClass} style={{ color: showMembershipPricing ? 'inherit' : '#707070' }}>
                            {showMembershipPricing ? 'Member Price' : 'Member Price'}
                        </span>
                    </Link>
                )}
            </div>
        );
    }

    // Full display for ProductDetailPage
    return (
        <div className={sectionClass}>
            <div className={priceRowClass}>
                <span className={showMembershipPricing ? strikethroughPriceClass : currentPriceClass}>
                    Retail: ${retailPrice.toFixed(2)}
                    {subscriptionInterval && (
                        <span className={styles.subscriptionInterval}>{subscriptionInterval}</span>
                    )}
                </span>
            </div>
            {pricesAreDifferent && (
                align === 'right' ? (
                    <Link to="/memberships" className={`${priceRowClass} ${styles.membershipPriceLink}`}>
                        <div className={membershipIndicatorClass}>
                            <img className={styles.alyzeLogo} src={showMembershipPricing ? AlyzePlusLogo : AlyzePlusLogoGray} alt="ALYZE+" />
                            <span className={styles.membershipText} style={{ color: showMembershipPricing ? 'inherit' : '#707070' }}>
                                Price
                            </span>
                        </div>
                        <span className={showMembershipPricing ? currentPriceClass : strikethroughPriceClass}>
                            ${membershipPrice.toFixed(2)}
                            {subscriptionInterval && (
                                <span className={styles.subscriptionInterval}>{subscriptionInterval}</span>
                            )}
                        </span>
                    </Link>
                ) : (
                    <Link to="/memberships" className={`${priceRowClass} ${styles.membershipPriceLink}`}>
                        <span className={showMembershipPricing ? currentPriceClass : strikethroughPriceClass}>
                            ${membershipPrice.toFixed(2)}
                            {subscriptionInterval && (
                                <span className={styles.subscriptionInterval}>{subscriptionInterval}</span>
                            )}
                        </span>
                        <div className={membershipIndicatorClass}>
                            <img className={styles.alyzeLogo} src={showMembershipPricing ? AlyzePlusLogo : AlyzePlusLogoGray} alt="ALYZE+" />
                            <span className={styles.membershipText} style={{ color: showMembershipPricing ? 'inherit' : '#707070' }}>
                                Price
                            </span>
                        </div>
                    </Link>
                )
            )}
        </div>
    );
};

// Helper function to get the current price based on membership status
export const getCurrentPrice = (hasMembership: boolean, retailPrice: number, membershipPrice: number): number => {
    return hasMembership ? membershipPrice : retailPrice;
};

// named-only exports

